import dayjs from 'dayjs';
import { produce } from 'immer';
import _ from 'lodash';

import { ConstraintType } from '../../api';
import {
  ExperimentAnalysisConfig,
  ExperimentAnalysisConfigView,
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getDefaultPopulationTarget,
  getDomain,
  getExperimentEndTimeConstraint,
  getExperimentStartTimeConstraint,
  getGeography,
  getRestrictByConstraint,
  isRestrictByConstraint,
  isTimeSliceStrategy,
  PopulationTargetType,
  TreatmentIds,
} from '../../constants';
import {
  Choice,
  Experiment,
  RegionalExperimentConfigValues,
  Variable,
} from '../../domain';
import { normalizeMetrics } from '../../utils';
import { ExperimentConfiguration } from '../../utils/types';
import { ExperimentAction } from '../actions/experiment/action-creators';
import { ExperimentActionType } from '../actions/experiment/action-types';
import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import { FACETS } from '@/constants/facets';
import {
  AdaptiveExperiment,
  Constraint,
  VariableInfo,
} from '@/lib/PlannerDTOs';

export interface ExperimentState {
  currentExperiment: Experiment | null;
  currentAdaptiveExperiment: AdaptiveExperiment | null;
  experiment: Experiment | null;
  adaptiveExperiment: AdaptiveExperiment | null;
  configurations: RegionalExperimentConfigValues[];
  variableTechFamilies: string[];
  variableFacets: string[];
  variables: VariableInfo[];
  currentAnalysis: ExperimentAnalysisConfigView | null;
  analysis: ExperimentAnalysisConfigView;
  isLoading: boolean;
  error: Error | null;
  validExperiment: boolean;
  validMetric: boolean;
}

export const initialExperimentState: ExperimentState = {
  currentExperiment: null,
  currentAdaptiveExperiment: null,
  experiment: null,
  adaptiveExperiment: null,
  configurations: [],
  variableTechFamilies: [],
  variableFacets: [],
  variables: [],
  currentAnalysis: null,
  analysis: null,
  isLoading: false,
  error: null,
  validExperiment: false,
  validMetric: false,
};

function experimentReducer(
  state: ExperimentState = initialExperimentState,
  action: ExperimentAction,
): ExperimentState {
  switch (action.type) {
    case ExperimentActionType.GET_EXPERIMENT: {
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    }

    case ExperimentActionType.CREATE_EXPERIMENT: {
      const payload = action.payload as {
        data: Experiment;
        isLoading: boolean;
        error: Error | null;
      };
      const payloadData = payload?.data as any;
      const primaryMetrics = payloadData?.analysisConfig?.primary_metrics || [];
      const hasPrimaryMetric = primaryMetrics.some((metric) => !!metric?.name);
      const isPrimaryMetricOptOutEnabled =
        !!payloadData?.metadata?.primaryMetricOptOutEnabled ||
        !!payloadData?.experiment?.metadata?.primaryMetricOptOutEnabled;
      const primaryMetricOptOutReason =
        payloadData?.metadata?.primaryMetricOptOutReason ??
        payloadData?.experiment?.metadata?.primaryMetricOptOutReason;
      const hasPrimaryMetricOptOutReason =
        typeof primaryMetricOptOutReason === 'string' &&
        primaryMetricOptOutReason.trim().length > 0;

      if (payload?.data) {
        payload.data.sampleSize = 1; // minimum sample size is 1

        if (!payload.data?.constraints?.length) payload.data.constraints = [];
      }

      return {
        ...state,
        currentExperiment: null,
        experiment: payload?.data || null,
        isLoading: payload?.isLoading,
        error: payload?.error,
        configurations: [
          {
            country_code: '',
            variables: [],
          },
        ],
        validExperiment: false, // because experiment detail are not input yet
        validMetric:
          hasPrimaryMetric ||
          (isPrimaryMetricOptOutEnabled && hasPrimaryMetricOptOutReason),
        currentAnalysis: null,
        analysis: {
          schemaVersion: 0,
          analysisConfig: {
            primary_metrics: [],
            secondary_metrics: [],
            guardrail_metrics: [],
            deepdive_all_dimensions: false,
            enable_triggered_analysis: false,
          } as ExperimentAnalysisConfig,
        },
      };
    }

    case ExperimentActionType.SET_EXPERIMENT: {
      const payload = action.payload as {
        data: Experiment;
        isLoading: boolean;
        error: Error | null;
      };

      return {
        ...state,
        currentExperiment: payload.data || null,
        experiment: payload.data || null,
        isLoading: payload?.isLoading,
        error: payload?.error,
      };
    }

    case ExperimentActionType.CLONE_EXPERIMENT: {
      const payload = action.payload as {
        data: Experiment;
        isLoading: boolean;
        error: Error | null;
      };

      const nextExperiment = produce(payload.data, (draft) => {
        draft.state = '';

        delete draft.approvalFlow;
        delete draft.autoAdaptive;
        delete draft.schedulingDefinition;
        delete draft.createdAt;

        if (draft.constraints) {
          draft.constraints = draft.constraints.filter(
            (constraint) => constraint.type !== ConstraintType.Targeting,
          );
        }
      });

      const nextAnalysis = produce(state.analysis, (draft) => {
        if (!draft) return;

        draft['analysisName'] = nextExperiment?.name;

        delete draft['analysisID'];
        delete draft['id'];
        delete draft['createdBy'];
        delete draft['createdAt'];
        delete draft['schemaVersion'];
        delete draft['supersetLink'];
        delete draft['updatedAt'];
        delete draft['updatedBy'];
      });

      return {
        ...state,
        analysis: nextAnalysis,
        currentExperiment: nextExperiment,
        experiment: nextExperiment,
        isLoading: payload?.isLoading,
        error: payload?.error,
      };
    }

    case ExperimentActionType.SET_CONFIGURATION: {
      const payload = action.payload as {
        data: RegionalExperimentConfigValues[];
      };

      return {
        ...state,
        configurations: payload.data || [],
      };
    }

    case ExperimentActionType.UPDATE_EXPERIMENT: {
      const payload = action.payload as {
        data: Experiment;
      };

      const nextExperiment = produce(payload.data, (draft) => {
        // Only recalculate endTime from dayLength for CUSTOM_TIME_SLICE strategy
        const currentStrategy = getAssignmentStrategy(state.configurations);
        const isCustomTimeSlice =
          currentStrategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE;

        if (isCustomTimeSlice && draft.dayLength && draft.startTime) {
          draft.endTime = draft.startTime
            .add(draft.dayLength, 'days')
            .subtract(1, 'minutes');
        }

        if (draft.startTime) {
          draft.startTime = dayjs(draft.startTime).startOf('minute');
          const unixStart = draft.startTime.unix();

          const startTimeConstraint = getExperimentStartTimeConstraint(draft);

          if (startTimeConstraint)
            startTimeConstraint.value = unixStart.toString();
          else
            draft.constraints = [
              ...(draft.constraints || []),
              {
                target: FACETS.Timestamp,
                op: '>',
                value: unixStart.toString(),
              },
            ];
        } else {
          const startTimeConstraint = getExperimentStartTimeConstraint(draft);

          if (startTimeConstraint)
            draft.constraints = draft.constraints.filter(
              (c) => c.target !== FACETS.Timestamp && c.op !== '>',
            );
        }

        if (draft.endTime) {
          draft.endTime = dayjs(draft.endTime).startOf('minute');
          const unixEnd = draft.endTime.unix();

          const endTimeConstraint = getExperimentEndTimeConstraint(draft);

          if (endTimeConstraint) endTimeConstraint.value = unixEnd.toString();
          else
            draft.constraints = [
              ...(draft.constraints || []),
              {
                target: FACETS.Timestamp,
                op: '<',
                value: unixEnd.toString(),
              },
            ];
        } else {
          const endTimeConstraint = getExperimentEndTimeConstraint(draft);

          if (endTimeConstraint)
            draft.constraints = draft.constraints.filter(
              (c) => c.target !== FACETS.Timestamp && c.op !== '<',
            );
        }
      });

      return {
        ...state,
        experiment: nextExperiment,
      };
    }

    case ExperimentActionType.UPDATE_CONFIGURATION: {
      const payload = action.payload as {
        data: RegionalExperimentConfigValues[];
      };

      const nextExperiment = produce(state.experiment, (draft) => {
        const countries = [];

        payload.data.forEach(
          (configuration: RegionalExperimentConfigValues) => {
            if (configuration?.country_code)
              countries.push(configuration.country_code);
          },
        );

        if (countries.length) {
          const restrictByConstraint = getRestrictByConstraint(draft);
          if (restrictByConstraint) {
            restrictByConstraint.value = `[${countries.join(',')}]`;
            restrictByConstraint.target = PopulationTargetType.COUNTRY;
          } else {
            draft.constraints = [
              ...(draft.constraints || []),
              {
                target: PopulationTargetType.COUNTRY,
                op: ConstraintOperator.IN.value,
                value: `[${countries.join(',')}]`,
              },
            ];
          }
        }
      });

      const nextConfigurations = produce(payload.data, (draft) => {
        const firstVariable = payload?.data?.[0]?.variables?.[0];

        draft.forEach((configuration: RegionalExperimentConfigValues) => {
          if (firstVariable) {
            // Synchronize all variables with the first variable's choice values
            configuration.variables.forEach(
              (variable: Variable, varIndex: number) => {
                if (varIndex === 0) return; // Skip the first variable (it's the reference)

                variable.choices.forEach(
                  (choice: Choice, choiceIndex: number) => {
                    const referenceChoice =
                      firstVariable.choices?.[choiceIndex];

                    if (referenceChoice) {
                      choice.groupConstraint = referenceChoice.groupConstraint;
                      choice.chance = referenceChoice.chance;
                      choice.span = referenceChoice.span;
                      choice.spanBuffer = referenceChoice.spanBuffer;
                    }
                  },
                );
              },
            );
          }
        });
      });

      return {
        ...state,
        experiment: nextExperiment,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.SET_ADAPTIVE_EXPERIMENT: {
      const payload = action.payload as {
        data: {
          adaptive_experiment: AdaptiveExperiment;
        };
        isLoading: boolean;
        error: Error | null;
      };

      return {
        ...state,
        currentAdaptiveExperiment: payload?.data?.adaptive_experiment || null,
        adaptiveExperiment: payload?.data?.adaptive_experiment || null,
        isLoading: payload?.isLoading,
        error: payload?.error,
      };
    }

    case ExperimentActionType.ADD_EXPERIMENT_VARIABLE: {
      const payload = action.payload as {
        variable: VariableInfo;
      };
      const experimentStrategy =
        getAssignmentStrategy(state.configurations) || '';

      const nextConfigurations = produce(state.configurations, (draft) => {
        const numOfChoices = draft?.[0]?.variables?.[0]?.choices?.length || 2;
        const firstVariableChoices = draft?.[0]?.variables?.[0]?.choices || [];

        draft.forEach((configuration: RegionalExperimentConfigValues) => {
          if (!configuration?.variables?.length) configuration.variables = [];

          const referenceChoices =
            configuration?.variables?.[0]?.choices || firstVariableChoices;

          configuration.variables.push({
            name: payload.variable?.name,
            strategy: experimentStrategy,
            geohashType: 5,
            choices: Array.from({ length: numOfChoices }, (_, index) => {
              const referenceChoice = referenceChoices[index];
              return {
                value: 0,
                string: '',
                treatment: index,
                groupConstraint: referenceChoice?.groupConstraint || {
                  target: getDomain(state.experiment?.domain)?.targetBy || '',
                  op: '',
                  value: '',
                  valueItemType: '',
                },
                chance: referenceChoice?.chance || 0,
                span: referenceChoice?.span || 0,
                spanBuffer: referenceChoice?.spanBuffer || 0,
                id: '',
                name:
                  index === 0 ? 'Control' : `Treatment ${TreatmentIds[index]}`,
              };
            }),
          });
        });
      });

      // Combine all variable-related updates into a single produce call for better performance
      const { nextVariables, nextVariablesTechFamilies, nextVariableFacets } =
        produce(
          {
            nextVariables: state.variables,
            nextVariablesTechFamilies: state.variableTechFamilies,
            nextVariableFacets: state.variableFacets,
          },
          (draft) => {
            // Add variable
            draft.nextVariables.push(payload.variable);

            // Add tech family if defined
            if (
              payload.variable?.techFamily &&
              !draft.nextVariablesTechFamilies.includes(
                payload.variable.techFamily,
              )
            ) {
              draft.nextVariablesTechFamilies.push(payload.variable.techFamily);
            }

            // Add unique facets
            const newFacets = payload.variable?.facets || [];
            newFacets.forEach((facet) => {
              if (!draft.nextVariableFacets.includes(facet)) {
                draft.nextVariableFacets.push(facet);
              }
            });
          },
        );

      const nextExperiment = produce(state.experiment, (draft) => {
        const experimentDomain = state?.experiment?.domain
          ? state?.experiment?.domain
          : payload.variable?.domain;
        const geography = getGeography(experimentDomain);
        const restrictByConstraint = getRestrictByConstraint(draft);
        draft.domain = experimentDomain;

        if (!draft?.constraints?.length) draft.constraints = [];

        if (!restrictByConstraint) {
          draft.constraints.push({
            target: getDefaultPopulationTarget(experimentDomain),
            op:
              geography === 'multi'
                ? ConstraintOperator.IN.value
                : ConstraintOperator.EQUALS.value,
            valueItemType: '',
            value: '',
          });
        } else {
          restrictByConstraint.op =
            geography === 'multi'
              ? ConstraintOperator.IN.value
              : ConstraintOperator.EQUALS.value;
        }
      });

      return {
        ...state,
        experiment: nextExperiment,
        configurations: nextConfigurations,
        variables: nextVariables,
        variableTechFamilies: nextVariablesTechFamilies,
        variableFacets: nextVariableFacets,
      };
    }

    case ExperimentActionType.DELETE_EXPERIMENT_VARIABLE: {
      const payload = action.payload as {
        variableName: string;
      };

      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.forEach(
          (configuration: ExperimentConfiguration, index: number) => {
            draft[index].variables = configuration.variables.filter(
              (variable) => variable.name !== payload.variableName,
            );
          },
        );
      });

      const nextExperiment = produce(state.experiment, (draft) => {
        if (nextConfigurations?.[0]?.variables?.length === 0) {
          draft.domain = null;
        }
      });

      // Remove the deleted variable from variables, tech families, and facets
      const deletedVariable = state.variables.find(
        (v) => v.name === payload.variableName,
      );

      const nextVariables = state.variables.filter(
        (v) => v.name !== payload.variableName,
      );

      // Remove tech family only if no other variable uses it
      const nextVariablesTechFamilies = deletedVariable?.techFamily
        ? state.variableTechFamilies.filter((tf) => {
            return (
              tf !== deletedVariable.techFamily ||
              nextVariables.some((v) => v.techFamily === tf)
            );
          })
        : state.variableTechFamilies;

      // Remove facets only if no other variable uses them
      const deletedFacets = new Set(deletedVariable?.facets || []);
      const nextVariableFacets = state.variableFacets.filter((facet) => {
        return (
          !deletedFacets.has(facet) ||
          nextVariables.some((v) => v.facets?.includes(facet))
        );
      });

      return {
        ...state,
        experiment: nextExperiment,
        configurations: nextConfigurations,
        variables: nextVariables,
        variableTechFamilies: nextVariablesTechFamilies,
        variableFacets: nextVariableFacets,
      };
    }

    case ExperimentActionType.ADD_TREATMENT: {
      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.forEach((configuration: ExperimentConfiguration) => {
          configuration?.variables?.forEach((variable: Variable) => {
            variable.choices.push({
              value: 0,
              string: '',
              treatment: variable.choices.length,
              name: `Treatment ${TreatmentIds[variable.choices.length]}`,
              groupConstraint: {
                target: getDomain(state.experiment?.domain)?.targetBy || '',
                op: '',
                value: '',
                valueItemType: '',
              },
              chance: 0,
              span: 0,
              spanBuffer: 0,
            });

            // when we add new treatment, we will just split it evenly
            const chance = Math.round(100 / variable.choices.length);

            variable.choices.forEach((choice: Choice) => {
              choice.chance = chance;
            });
          });
        });
      });

      return {
        ...state,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.DELETE_TREATMENT: {
      const payload = action.payload as {
        index: number;
      };

      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.forEach((configuration: ExperimentConfiguration) => {
          configuration?.variables?.forEach((variable: Variable) => {
            variable.choices = variable.choices.filter(
              (choice: Choice) => choice.treatment !== payload.index,
            );
          });
        });
      });

      const nextExperiment = produce(state.experiment, (draft) => {
        const constraints = draft?.constraints || [];

        const endConstraint = constraints.find(
          (constraint) =>
            constraint.target === FACETS.Timestamp && constraint.op === '<',
        );

        if (endConstraint) {
          endConstraint.value = null;
        } else {
          constraints.push({
            target: FACETS.Timestamp,
            op: '<',
            value: null,
          });
        }

        draft.constraints = constraints;
        draft.endTime = null;
      });

      return {
        ...state,
        experiment: nextExperiment,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.ADD_CONFIGURATION: {
      const nextConfigurations = produce(state.configurations, (draft) => {
        const variables: Variable[] = [];

        draft?.[0]?.variables?.forEach((variable: Variable) => {
          variables.push({
            ...variable,
            choices: [...variable.choices],
          });
        });

        draft.push({
          country_code: '',
          variables: variables,
        });
      });

      const nextExperiment = produce(state.experiment, (draft) => {
        const restrictByConstraint = getRestrictByConstraint(draft);

        if (restrictByConstraint) {
          if (restrictByConstraint.target !== PopulationTargetType.COUNTRY) {
            restrictByConstraint.target = PopulationTargetType.COUNTRY;
            restrictByConstraint.value = '';
          }
        } else {
          draft.constraints = [
            ...(draft.constraints || []),
            {
              target: PopulationTargetType.COUNTRY,
              op: ConstraintOperator.IN.value,
              value: '',
            },
          ];
        }
      });

      return {
        ...state,
        configurations: nextConfigurations,
        experiment: nextExperiment,
      };
    }

    case ExperimentActionType.DELETE_CONFIGURATION: {
      const payload = action.payload as {
        index: number;
      };

      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.splice(payload.index, 1);
      });

      return {
        ...state,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.CLONE_CONFIGURATION: {
      const payload = action.payload as {
        fromIndex: number;
        toIndex: number;
      };

      const nextConfigurations = produce(state.configurations, (draft) => {
        const fromConfiguration = draft[payload.fromIndex];
        const toConfiguration = draft[payload.toIndex];

        toConfiguration.variables = fromConfiguration.variables;
      });

      return {
        ...state,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.EDIT_TREATMENT: {
      const payload = action.payload as {
        index: number;
        name: string;
      };

      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.forEach((configuration: ExperimentConfiguration) => {
          configuration?.variables?.forEach((variable: Variable) => {
            variable.choices[payload.index].name = payload.name;
          });
        });
      });

      return {
        ...state,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.UPDATE_ASSIGNMENT_STRATEGY: {
      const payload = action.payload as {
        strategy: string;
      };

      const currentExperimentStrategy = getAssignmentStrategy(
        state.configurations,
      );
      const isUniformChoice =
        payload.strategy === ExperimentAssignmentStrategy.UNIFORM_CHOICE;
      const isExplicitGroup =
        payload.strategy === ExperimentAssignmentStrategy.EXPLICIT_GROUP;

      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.forEach((configuration: ExperimentConfiguration) => {
          configuration?.variables?.forEach((variable: Variable) => {
            variable.strategy = payload.strategy;

            variable.choices.forEach((choice: Choice) => {
              if (isUniformChoice) {
                choice.chance = Math.round(100 / variable.choices.length);
              }

              if (isExplicitGroup) {
                const target =
                  getDomain(state.experiment?.domain)?.targetBy || '';
                if (!choice?.groupConstraint) {
                  choice['groupConstraint'] = {
                    target: target,
                    op: '',
                    value: '',
                    valueItemType: '',
                  };
                } else {
                  choice.groupConstraint.target = target;
                }
              }
            });
          });
        });
      });

      const nextExperiment = produce(state.experiment, (draft) => {
        if (isTimeSliceStrategy(payload.strategy) || isExplicitGroup) {
          draft.sampleSize = 100;
        }

        if (
          !isTimeSliceStrategy(currentExperimentStrategy) &&
          isTimeSliceStrategy(payload.strategy)
        ) {
          const endConstraint = getExperimentEndTimeConstraint(draft);
          if (endConstraint) {
            endConstraint.value = null;
          } else {
            draft.constraints.push({
              target: FACETS.Timestamp,
              op: '<',
              value: null,
            });
          }

          draft.endTime = null;
        }
      });

      // Clear control_metrics when switching from timeslice to non-timeslice strategy
      // Control metrics are only applicable for timeslice experiments
      const nextAnalysis =
        isTimeSliceStrategy(currentExperimentStrategy) &&
        !isTimeSliceStrategy(payload.strategy)
          ? produce(state.analysis, (draft) => {
              if (draft?.analysisConfig?.control_metrics) {
                draft.analysisConfig.control_metrics = [];
              }
            })
          : state.analysis;

      return {
        ...state,
        experiment: nextExperiment,
        configurations: nextConfigurations,
        analysis: nextAnalysis,
      };
    }

    case ExperimentActionType.SPLIT_TREATMENTS_EVENLY: {
      const nextConfigurations = produce(state.configurations, (draft) => {
        draft.forEach((configuration: ExperimentConfiguration) => {
          configuration?.variables?.forEach((variable: Variable) => {
            variable.strategy = ExperimentAssignmentStrategy.UNIFORM_CHOICE;

            const chance = Math.round(100 / variable.choices.length);

            variable.choices.forEach((choice: Choice) => {
              choice.chance = chance;
            });
          });
        });
      });

      return {
        ...state,
        configurations: nextConfigurations,
      };
    }

    case ExperimentActionType.SET_VARIABLE_TECH_FAMILIES: {
      const payload = action.payload as {
        techFamilies: string[];
      };

      return {
        ...state,
        variableTechFamilies: payload.techFamilies,
      };
    }

    case ExperimentActionType.SET_VARIABLES: {
      const payload = action.payload as {
        variables: VariableInfo[];
      };

      const techFamilies = payload?.variables?.map(
        (variable) => variable?.techFamily,
      );

      const variableFacets = payload?.variables?.flatMap(
        (variable) => variable?.facets ?? [],
      );

      return {
        ...state,
        variables: payload.variables,
        variableTechFamilies: techFamilies,
        variableFacets: variableFacets,
      };
    }

    case ExperimentActionType.SET_ANALYSIS: {
      const payload = action.payload as {
        analysis: ExperimentAnalysisConfigView;
        isLoading: boolean;
        error: Error | null;
      };

      const nextAnalysis = produce(payload?.analysis, (draft) => {
        if (draft?.analysisConfig) {
          draft.analysisConfig = {
            ...draft.analysisConfig,
            primary_metrics: normalizeMetrics(
              draft.analysisConfig?.primary_metrics,
            ),
            secondary_metrics: normalizeMetrics(
              draft.analysisConfig?.secondary_metrics,
            ),
            guardrail_metrics: normalizeMetrics(
              draft.analysisConfig?.guardrail_metrics,
            ),
            deepdive_all_dimensions:
              draft.analysisConfig?.deepdive_all_dimensions || false,
            enable_triggered_analysis:
              draft.analysisConfig?.enable_triggered_analysis || false,
          };
        }
      });

      const nextCurrentAnalysis = produce(nextAnalysis, () => {});

      return {
        ...state,
        currentAnalysis: nextCurrentAnalysis,
        analysis: nextAnalysis,
        isLoading: payload.isLoading,
        error: payload.error,
      };
    }

    case ExperimentActionType.UPDATE_ANALYSIS_CONFIG: {
      const payload = action.payload as {
        analysisConfig: any;
      };

      return {
        ...state,
        analysis: {
          ...state.analysis,
          analysisConfig: payload.analysisConfig,
        },
      };
    }

    case ExperimentActionType.UPDATE_RESTRICT_BY_CONSTRAINT: {
      const payload = action.payload as {
        constraint: Constraint;
      };

      const nextExperiment = produce(state.experiment, (draft) => {
        const constraints = draft?.constraints || [];
        const restrictByConstraintIndex = constraints.findIndex((constraint) =>
          isRestrictByConstraint(constraint),
        );

        if (restrictByConstraintIndex > -1)
          draft.constraints[restrictByConstraintIndex] = payload.constraint;
        else draft.constraints = [...constraints, payload.constraint];
      });

      return {
        ...state,
        experiment: nextExperiment,
      };
    }

    case ExperimentActionType.UPDATE_VALID_EXPERIMENT: {
      const payload = action.payload as {
        valid: boolean;
      };

      return {
        ...state,
        validExperiment: payload.valid,
      };
    }

    case ExperimentActionType.UPDATE_VALID_METRIC: {
      const payload = action.payload as {
        valid: boolean;
      };

      return {
        ...state,
        validMetric: payload.valid,
      };
    }

    case ExperimentActionType.ADD_CONSTRAINT: {
      const payload = action.payload as {
        constraint: Constraint;
      };

      const nextExperiment = produce(state.experiment, (draft) => {
        draft.constraints = [...(draft.constraints || []), payload.constraint];
      });

      return {
        ...state,
        experiment: nextExperiment,
      };
    }

    case ExperimentActionType.DELETE_CONSTRAINT: {
      const payload = action.payload as {
        index: number;
      };

      const nextExperiment = produce(state.experiment, (draft) => {
        draft.constraints = draft.constraints.filter(
          (_, i) => i !== payload.index,
        );
      });

      return {
        ...state,
        experiment: nextExperiment,
      };
    }

    case ExperimentActionType.UPDATE_CONSTRAINT: {
      const payload = action.payload as {
        index: number;
        constraint: Constraint;
      };

      const nextExperiment = produce(state.experiment, (draft) => {
        draft.constraints[payload.index] = payload.constraint;
      });

      return {
        ...state,
        experiment: nextExperiment,
      };
    }

    case ExperimentActionType.SET_CONSTRAINTS: {
      const payload = action.payload as {
        constraints: Constraint[];
      };

      return {
        ...state,
        experiment: {
          ...state.experiment,
          constraints: payload.constraints,
        },
      };
    }

    case ExperimentActionType.SET_SLOTTING_BY_GROUP: {
      const payload = action.payload as {
        slottingByGroup: Constraint;
      };

      return {
        ...state,
        experiment: {
          ...state.experiment,
          byGroup: payload.slottingByGroup,
          sampleSize: null,
        },
      };
    }

    default:
      return state;
  }
}

export default experimentReducer;
